/**
 * <s-product-gallery> — one-image-at-a-time product media carousel.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontal snap-scroll track.
 *   [data-gallery-slide]          — each slide; aria-label carries the translated "Image n of N".
 *   button[command="--prev|--next"] with commandfor pointing at this element's id.
 *   button[command="--go"][data-index] — thumbnails.
 *   [data-gallery-status]         — polite live region for slide announcements.
 *   [data-gallery-counter]        — visible "n / N" counter.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #index = 0;
  #announceRafId = 0;
  #userNavigated = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    if (!this.track || this.slides.length === 0) return;

    this.addEventListener('command', this.#handleCommand, opts);
    this.track.addEventListener('keydown', this.#handleKeydown, opts);

    // The visible slide is whichever one fills most of the track; scroll, swipe and buttons all resolve through this.
    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: this.track,
      threshold: 0.6,
    });
    for (const slide of this.slides) this.#observer.observe(slide);

    this.#update(0, false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#announceRafId);
  }

  #handleCommand = (event) => {
    const { command, source } = event;
    if (command === '--prev') this.goTo(this.#index - 1);
    else if (command === '--next') this.goTo(this.#index + 1);
    else if (command === '--go') this.goTo(Number(source?.dataset.index ?? 0));
  };

  #handleKeydown = (event) => {
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.#index + (rtl ? -1 : 1));
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.#index + (rtl ? 1 : -1));
    }
  };

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = this.slides.indexOf(entry.target);
      if (index !== -1 && index !== this.#index) this.#update(index, this.#userNavigated);
    }
  };

  goTo(index) {
    const target = Math.max(0, Math.min(this.slides.length - 1, index));
    const slide = this.slides[target];
    if (!slide) return;

    this.#userNavigated = true;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll only the track so the page never jumps; inline offset works in both reading directions.
    const trackRect = this.track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    this.track.scrollBy({ left: slideRect.left - trackRect.left, behavior: reduce ? 'auto' : 'smooth' });
  }

  #update(index, announce) {
    this.#index = index;
    const count = this.slides.length;

    this.slides.forEach((slide, i) => {
      const active = i === index;
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    for (const thumb of this.querySelectorAll('button[command="--go"]')) {
      const active = Number(thumb.dataset.index) === index;
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    }

    const prev = this.querySelector('button[command="--prev"]');
    const next = this.querySelector('button[command="--next"]');
    if (prev) prev.disabled = index === 0;
    if (next) next.disabled = index === count - 1;

    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = `${index + 1} / ${count}`;

    if (announce) this.#announce(this.slides[index].getAttribute('aria-label') ?? '');
  }

  #announce(text) {
    const status = this.querySelector('[data-gallery-status]');
    if (!status) return;
    cancelAnimationFrame(this.#announceRafId);
    status.textContent = '';
    this.#announceRafId = requestAnimationFrame(() => {
      status.textContent = text;
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
